import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { useMount } from 'react-use';

import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { Team } from '@yandex-int/k-common/typings';
import { Spin, Button, Text, SimpleText, Checkbox, Modal, H3, ModalCloseProps } from 'platform-components/common';
import { CHECKBOX_TYPES } from 'platform-components/constants';
import { CloseButton } from 'platform-components/src/components/close-button/_view/close-button_view_outer';

import ClassAvatar from 'common.components/class-avatar/class-avatar';
import { isMesTeam } from 'common.components/classes/utils';
import ExternalLmsBadge from 'common.components/external-lms-badge/external-lms-badge';
import * as teacherCoursesActions from 'store/teacher-courses/actions';
import {
    getAreClassCoursesLoading,
    getClassesWithCurrentSubject,
    getClassesIdToLoad,
} from 'store/teacher-courses/selectors';
import { getTrimmedName } from 'utils/get-trimmed-name';
import { getTeamIdFromPath, getSubjectFromPath } from 'utils/path';

import actions from './actions';
import { MAX_CLASS_NAME_LENGTH } from './constants';
import * as selectors from './selectors';

import './copy-lesson-modal.scss';

export const CopyLessonModal = () => {
    const intl = useIntl();
    const history = useHistory();
    const dispatch = useDispatch();

    const currentTeamId = getTeamIdFromPath();
    const subject = getSubjectFromPath();

    const teams = useSelector((storeState) => getClassesWithCurrentSubject(storeState, { subject }));
    const teamIdsToLoad = useSelector(getClassesIdToLoad);
    const areClassCoursesLoading = useSelector(getAreClassCoursesLoading);
    const shouldShowModal = useSelector(selectors.getShouldShowModal);
    const copyStatus = useSelector(selectors.getCopyStatus);

    const [selectedClasses, setSelectedClasses] = useState<Array<number>>(
        currentTeamId && teams.find((t) => t.id === currentTeamId) ? [currentTeamId] : []
    );

    useMount(() => {
        if (teamIdsToLoad.length > 0) {
            dispatch(teacherCoursesActions.getTeacherCoursesByClassRequest(teamIdsToLoad));
        }
    });

    useEffect(() => {
        if (shouldShowModal) {
            setSelectedClasses(currentTeamId && teams.find((t) => t.id === currentTeamId) ? [currentTeamId] : []);
        }
    }, [currentTeamId, shouldShowModal, teams]);

    const toggleClassSelection = useCallback(
        (classData: Team) => {
            const { id } = classData;

            const list = [...selectedClasses];
            const index = list.indexOf(id);

            if (index >= 0) {
                list.splice(index, 1);
            } else {
                list.push(id);
            }

            setSelectedClasses(list);
        },
        [selectedClasses]
    );

    const handleSubmit = useCallback(() => {
        dispatch(
            actions.copyLessonToTeamsRequest({
                teamIds: selectedClasses,
                intl,
                //@ts-ignore почему-то не сходятся тайпинги
                history,
            })
        );
    }, [dispatch, history, intl, selectedClasses]);

    const handleCloseModal = useCallback(() => {
        dispatch(actions.closeModal());
    }, [dispatch]);

    const isAnySelected = selectedClasses.length > 0;
    const copyInProgress = copyStatus === LOADING_STATUSES.LOADING;

    return (
        <Modal
            Close={(closeProps: ModalCloseProps) => (
                <CloseButton {...closeProps} position={CloseButton.POSITION.ABSOLUTE} />
            )}
            hasClose
            mix="copy-lesson-modal"
            onRequestClose={handleCloseModal}
            opened={shouldShowModal}
            theme={Modal.THEMES.TEACHER}
        >
            <Modal.Header>
                <H3 id="copyLessonModal.description" type={Text.TYPE.MARKDOWN} weight={Text.WEIGHT.M} />
            </Modal.Header>
            <Modal.Content>
                {areClassCoursesLoading ? (
                    <Spin center />
                ) : (
                    <div className="copy-lesson-modal__classes">
                        {teams.map((team) => {
                            const trimmedClassname = getTrimmedName(team.name, MAX_CLASS_NAME_LENGTH);
                            const isCurrentClassFromMes = isMesTeam(team);

                            return (
                                <div className="copy-lesson-modal__class" key={team.id}>
                                    <div className="copy-lesson-modal__class-name">
                                        <ClassAvatar id={team.id} level={team.level} size={ClassAvatar.SIZES.SMALL} />
                                        <Text
                                            id="copyLessonModal.className"
                                            values={{ level: team.level, name: trimmedClassname }}
                                        />
                                    </div>
                                    {isCurrentClassFromMes && <ExternalLmsBadge textId="class.badge.mes" />}
                                    <Checkbox
                                        checked={selectedClasses.some((id) => id === team.id)}
                                        key={team.id}
                                        mix="copy-lesson-modal__checkbox"
                                        onChange={() => toggleClassSelection(team)}
                                        type={CHECKBOX_TYPES.GREY}
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}
            </Modal.Content>
            <Modal.Footer>
                <Button
                    className="copy-lesson-modal__button"
                    disabled={!isAnySelected}
                    onClick={handleSubmit}
                    progress={copyInProgress}
                >
                    <SimpleText id="copyLessonModal.submitButon" type={SimpleText.TYPE.MARKDOWN} />
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CopyLessonModal;
